import { BackButton } from '@/src/common/components/BackButton'
import { Routes } from '@/src/common/enums/routes'
import { useBasicInfoCardApplicationStore } from '@/src/common/store/useBasicInfoCardApplicationStore'
import { usePiiInformationStore } from '@/src/common/store/usePiiInformationStore'
import { Button } from '@/src/common/components/Button'
import { ScrollView, Text, StyleSheet, Platform } from 'react-native'
import { type ComponentProps, useState, useEffect, useMemo } from 'react'
import { Switch } from '@/src/common/components/Switch'
import { UPDATE_USER_INFORMATION } from '@/src/common/gql/mutations/user'
import { useMutation } from '@apollo/client'
import useAddAddressInformationStore from '@/src/common/store/useAddAddressInformationStore'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useRouter } from 'expo-router'
import * as Statsig from '../../statsig'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { graphql } from '@api/mesa'
import { useCardApplicationReferralCodeStore } from '@/src/common/store/useCardApplicationReferralCodeStore'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { isE2E } from '@/src/common/environment'
import { useSardine } from '@/src/services/Sardine'
import { withRecaptchaVerification } from '@/src/common/utils/withRecaptchaVerification'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import { CardApplicationWrapper } from './cardApplicationWrapper'
import { mesaColorNames, typography } from '@/src/common/theme'
import { View, Image } from 'tamagui'
import { groupNames } from '@/tamagui.config'
import * as E2E from '../../e2e'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import * as Mortgage from '@/src/experiences/mortgage'

function Link(props: ComponentProps<typeof Text> & { link: string }) {
  return (
    <Text
      {...props}
      style={styles.linkStyle}
      onPress={() => void openInAppBrowser(props.link, ' ')}
    />
  )
}

export function ApplicationCardHolderAgreement() {
  const router = useRouter()
  const logError = useLogError()
  const Alert = useAlert(ApplicationCardHolderAgreement.name)
  const { basicInformation, setBasicInformation } = useBasicInfoCardApplicationStore()
  const { userPiiInformation, setUserPiiInformation } = usePiiInformationStore()
  const { setUserPrimaryAddress, setUserMailingAddress, userPrimaryAddress, userMailingAddress } =
    useAddAddressInformationStore()
  const { referralCode } = useCardApplicationReferralCodeStore()

  const cardPolicyAndProcedures = Statsig.ParameterStores.useCardPolicyAndProcedures()
  const { updateOptions } = useSardine()

  const recaptchaEnabled = useFeatureGate(FeatureGates.RecaptchaEnabled).featureGate?.value

  const { data: userData } = useLoggedInQuery(GET_USER_INFO, {
    fetchPolicy: 'cache-first',
  })
  const sourcePartnerName = userData?.getUserInfo.sourcePartnerName ?? 'default'
  const parsedResult = Mortgage.Statsig.Store.MortgageModuleVariantName.safeParse(sourcePartnerName)
  const parsedSourcePartnerName = parsedResult.success ? parsedResult.data : 'default'

  const links = {
    MesaCardholderAgreement: (
      <Link link={cardPolicyAndProcedures.get('mesa_cardholder_agreement', '')}>
        Mesa Cardholder Agreement
      </Link>
    ),
    LakeviewCardholderAgreement: (
      <Link link={cardPolicyAndProcedures.get('partner')?.lakeview?.cardholder_agreement ?? ''}>
        Lakeview Cardholder Agreement
      </Link>
    ),
    CarringtonCardholderAgreement: (
      <Link link={cardPolicyAndProcedures.get('partner')?.carrington?.cardholder_agreement ?? ''}>
        Carrington Cardholder Agreement
      </Link>
    ),
    MesaPrivacyPolicy: (
      <Link link={cardPolicyAndProcedures.get('mesa_privacy_policy', '')}>Mesa Privacy Policy</Link>
    ),
    CelticBankPrivacyNotice: (
      <Link link={cardPolicyAndProcedures.get('celtic_bank_privacy_policy', '')}>
        Celtic Bank Privacy Notice
      </Link>
    ),
    MesaPrivacyNotice: (
      <Link link={cardPolicyAndProcedures.get('mesa_privacy_notice', '')}>Mesa Privacy Notice</Link>
    ),
    TermsOfService: (
      <Link link={cardPolicyAndProcedures.get('terms_of_service', '')}>Terms of Service</Link>
    ),
    ESignConsentPolicy: (
      <Link link={cardPolicyAndProcedures.get('e-sign_consent_policy', '')}>
        E-Sign Consent Policy
      </Link>
    ),
    CreditReportAuthorizationNotice: (
      <Link link={cardPolicyAndProcedures.get('credit_report_authorization_notice', '')}>
        Credit Report Authorization Notice
      </Link>
    ),
    CardholderRewardsTermsOfUse: (
      <Link link={cardPolicyAndProcedures.get('cardholder_rewards_terms_of_use', '')}>
        Cardholder Rewards Terms of Use
      </Link>
    ),
    LakeviewRewardsTermsOfUse: (
      <Link
        link={
          cardPolicyAndProcedures.get('partner')?.lakeview?.rewards_terms_of_use ||
          'https://www.mesamember.com/legal/lakeview/mesa-homeowners-visa-r-signature-preferred-credit-card-for-lakeview-membership-terms-and-conditions'
        }
      >
        Lakeview Rewards Terms of Use
      </Link>
    ),
    CarringtonRewardsTermsOfUse: (
      <Link link={cardPolicyAndProcedures.get('partner')?.carrington?.rewards_terms_of_use ?? ''}>
        Carrington Rewards Terms of Use
      </Link>
    ),
    PatriotActNotice: (
      <Link link={cardPolicyAndProcedures.get('usa_patriot_act_notice', '')}>
        USA PATRIOT Act Notice
      </Link>
    ),
  } as const satisfies Record<string, React.JSX.Element>

  function alertErrorToUser() {
    Alert.alert(
      'Sorry!',
      "We aren't able to process your application at this time. Please try again later.",
    )
  }

  const [createCardApplication, { loading: createCardApplicationLoading }] = useMutation(
    graphql(`
      mutation ApplyForCreditCardProduct($input: ApplyForCreditCardProductRequestDto!) {
        applyForCreditCardProduct(applyForCreditCardProduct: $input) {
          status
          message
        }
      }
    `),
    {
      onError(error) {
        logError(error)
        alertErrorToUser()
      },
      onCompleted({ applyForCreditCardProduct }) {
        if (applyForCreditCardProduct.status === 'ERROR') {
          Alert.alert(
            "We aren't able to process your application at this time",
            applyForCreditCardProduct.message,
          )
          // todo: remove this once we properly differentiate between unexpected errors and user errors.
          // still though, user errors should be exceedingly rare at the application submission stage
          // because we'd ideally have already validated all the data before getting to this point
          logError('applyForCreditCardProduct errored', applyForCreditCardProduct, {
            basicInformation,
            userPiiInformation,
            referralCode,
            userPrimaryAddress,
            userMailingAddress,
          })
          return
        }
        cleanAllStore()
        router.replace(Routes.ApplicationProcessing.route)
      },
    },
  )

  const [updateUserInformation, { loading: updateUserLoading }] = useMutation(
    UPDATE_USER_INFORMATION,
    {
      onError(error) {
        logError(error)
        alertErrorToUser()
      },
      async onCompleted() {
        if (!userPiiInformation) {
          logError(new Error('User PII Information is missing', { cause: userPiiInformation }))
          alertErrorToUser()
          return
        }

        if (!userPiiInformation.totalAnnualIncome) {
          logError(new Error('Annual Income is missing', { cause: userPiiInformation }))
        }

        if (!userPiiInformation.monthlyHousingPayment) {
          logError(new Error('Monthly Housing Payment is missing', { cause: userPiiInformation }))
        }

        await createCardApplication({
          variables: {
            input: {
              ssn: userPiiInformation.ssn,
              dateOfBirth: userPiiInformation.dob.toISOString(),
              income: +userPiiInformation.totalAnnualIncome * 100, // to get whole number as integer including cents
              consentTimestamp: new Date().toISOString(),
              monthlyHousingPayment: +userPiiInformation.monthlyHousingPayment * 100, // to get whole number as integer including cents
              referralCode: referralCode?.trim(),
            },
          },
          ...(recaptchaEnabled
            ? {
                context: {
                  headers: {
                    'X-Recaptcha-Token': await withRecaptchaVerification('CARD_APPLICATION'),
                  },
                },
              }
            : undefined),
        })
      },
    },
  )

  const [agreementConfirmation, setAgreementConfirmation] = useState(false)

  function cleanAllStore() {
    setBasicInformation(undefined)
    setUserPiiInformation(undefined)
    setUserPrimaryAddress(undefined)
    setUserMailingAddress(undefined)
  }

  useEffect(() => {
    void updateOptions({ flow: 'application-card-holder-agreement' })
  }, [updateOptions])

  const rewardsTermsOfUseLink = useMemo(() => {
    switch (parsedSourcePartnerName) {
      case 'carrington':
        return links.CarringtonRewardsTermsOfUse
      case 'lakeview':
        return links.LakeviewRewardsTermsOfUse
      case 'default':
        return links.CardholderRewardsTermsOfUse
      default:
        return links.CardholderRewardsTermsOfUse
    }
  }, [parsedSourcePartnerName])

  const cardholderAgreementLink = useMemo(() => {
    switch (parsedSourcePartnerName) {
      case 'carrington':
        return links.CarringtonCardholderAgreement
      case 'lakeview':
        return links.LakeviewCardholderAgreement
      default:
        return links.MesaCardholderAgreement
    }
  }, [parsedSourcePartnerName])

  return (
    <CardApplicationWrapper>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={Platform.OS === 'web' && { overflow: 'visible' }}
      >
        <BackButton
          href="/main/card-application/applicationConfirmation"
          text="Back"
          testID={E2E.Config.Ids.Press.CardHolderAgreementBack}
        />
        <View style={styles.headerText}>
          <Text style={typography['heading/heading2']}>
            Last step, please review our cardholder agreement
          </Text>
        </View>
        <Text style={[typography['paragraph/md'], styles.instructionText]}>
          Tap document and scroll through the entire document to review
        </Text>
        {!isE2E && (
          <View
            onPress={() => {
              const cardHolderAgreementPdf = cardPolicyAndProcedures.get(
                'mesa_cardholder_agreement',
                'https://cdn.prod.website-files.com/66ba8f888ae5ad6954fd9036/67dc3b098f0524de754df8e2_CP-2%20CHA%20Mesa%20Homeowners%20Card.docx.pdf',
              )
              if (cardHolderAgreementPdf) {
                void openInAppBrowser(cardHolderAgreementPdf, 'Mesa Cardholder Agreement')
              }
            }}
            group={groupNames.cardHolderAgreement}
            animation="quick"
            pressStyle={{ opacity: 0.3 }}
          >
            <Image
              style={{
                width: '100%',
                height: 300,
                borderColor: mesaColorNames['Greyscale/400 #A4AAAC'],
                borderWidth: 1,
                borderRadius: 8,
                marginBottom: 16,
              }}
              animation="quick"
              $group-cardHolderAgreement-hover={{ opacity: 0.1 }}
              source={{
                uri: 'https://assets.mesamember.com/mesa_card_holder_agreement_preview.png',
              }}
            />
            <View
              style={[
                StyleSheet.absoluteFillObject,
                {
                  bottom: 16,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: 8,
                  cursor: 'pointer',
                },
              ]}
              opacity={0}
              animation="quick"
              $group-cardHolderAgreement-hover={{ opacity: 1 }}
            >
              <Text style={[typography['label/md-regular'], { color: 'white' }]}>
                View Agreement →
              </Text>
            </View>
          </View>
        )}
        <Switch
          checked={agreementConfirmation}
          onCheckedChange={setAgreementConfirmation}
          testID={E2E.Config.Ids.Switch.ConsentToTerms}
          label={{
            render: ({ switchComponent }) => (
              <>
                {switchComponent}
                <Text
                  style={[
                    styles.agreementConfirmationText,
                    { color: mesaColorNames['Greyscale/400 #A4AAAC'] },
                  ]}
                >
                  In order to proceed with your application you must confirm that you have read and
                  consent to the following disclosures by toggling the associated switch.
                </Text>
              </>
            ),
          }}
        />
        <View>
          <View>
            <Text style={[styles.agreementConfirmationText, { paddingTop: 10 }]}>
              By tapping "Apply", I acknowledge that I am providing written instructions under the
              Fair Credit Reporting Act authorizing Mesa and its affiliated partners to obtain my
              credit report from one or more of the credit reporting agencies. By submitting your
              application, your full credit report will be requested from one or more of the
              consumer reporting agencies, which may negatively affect your credit score. I
              acknowledge that I have read and agree to all of the statements below:
            </Text>
          </View>
          <View style={{ marginTop: 10 }}>
            {[
              <>
                I have received, read, and understand the {cardholderAgreementLink} and agree that
                if I am approved for and accept the Mesa Visa Signature Card, I will be bound to the
                terms and conditions set forth in the {cardholderAgreementLink}.
              </>,
              <>
                I have read, understand, and agree to the {links.MesaPrivacyPolicy},{' '}
                {links.CelticBankPrivacyNotice}, {links.MesaPrivacyNotice}, {links.TermsOfService},{' '}
                {links.ESignConsentPolicy}, {links.CreditReportAuthorizationNotice} and{' '}
                {rewardsTermsOfUseLink}.
              </>,
              <>I have received, read, and understand the {links.PatriotActNotice}.</>,
            ].map((item, index) => (
              <View key={index} style={styles.itemContainer}>
                <View style={styles.bullet} />
                <Text style={styles.itemText}>{item}</Text>
              </View>
            ))}
          </View>
        </View>
        <View style={styles.instructionText}></View>
      </ScrollView>
      <View style={{ marginBottom: 10 }}>
        <Button
          title="Apply"
          loading={updateUserLoading || createCardApplicationLoading}
          onPress={() => {
            if (!basicInformation || !basicInformation.firstName || !basicInformation.lastName) {
              logError(new Error('Basic information is missing', { cause: basicInformation }))
              alertErrorToUser()
              return
            }

            void updateUserInformation({
              variables: {
                firstName: basicInformation.firstName,
                lastName: basicInformation.lastName,
              },
            })
          }}
          disabled={!agreementConfirmation || updateUserLoading || createCardApplicationLoading}
        />
      </View>
    </CardApplicationWrapper>
  )
}

const styles = StyleSheet.create({
  headerText: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 32,
  },
  instructionText: {
    marginVertical: 16,
    marginBottom: 16,
    lineHeight: 24,
    color: mesaColorNames['Greyscale/400 #A4AAAC'],
  },
  agreementConfirmationText: {
    ...typography['paragraph/md'],
    color: mesaColorNames['Mesa - Brand/Blackish #222222'],
    flex: 1,
  },
  itemContainer: {
    flexDirection: 'row',
    marginVertical: 5,
    alignItems: 'flex-start',
  },
  bullet: {
    width: 5,
    height: 5,
    borderRadius: 5,
    backgroundColor: mesaColorNames['Greyscale/400 #A4AAAC'],
    marginLeft: 10,
    marginRight: 5,
    marginTop: 10,
  },
  itemText: {
    ...typography['paragraph/xs'],
    fontSize: 16,
    color: mesaColorNames['Greyscale/400 #A4AAAC'],
    flexShrink: 1,
  },
  linkStyle: {
    textDecorationLine: 'underline',
    color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
  },
} as const)
